import React from 'react';
import { ToggleGroup } from '@plerionhq/design-system/experimental';
import { Tooltip } from '@plerionhq/design-system';

import { useTranslation } from 'react-i18next';

export type ChartType = 'area' | 'bar' | 'line';

interface ChartTypeSelectorProps {
  value: ChartType;
  onChange: (type: ChartType) => void;
  className?: string;
}

const ChartTypeIcon: React.FC<{ type: ChartType }> = ({ type }) => {
  const { t } = useTranslation('pleri/pages');
  switch (type) {
    case 'area':
      return (
        <Tooltip label={t('areaChart')}>
          <svg
            className="h-5 w-5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M3 3v18h18" strokeLinecap="round" strokeLinejoin="round" />
            <path
              d="M3 17l4-4 5 3 6-8 3-3v12H3z"
              fill="currentColor"
              fillOpacity="0.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M3 17l4-4 5 3 6-8 3-3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Tooltip>
      );
    case 'bar':
      return (
        <Tooltip label={t('barChart')}>
          <svg
            className="h-5 w-5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M3 3V21H21" strokeLinecap="round" strokeLinejoin="round" />
            <rect
              x="6"
              y="8"
              width="3"
              height="10"
              fill="currentColor"
              fillOpacity="0.2"
            />
            <rect
              x="11"
              y="4"
              width="3"
              height="14"
              fill="currentColor"
              fillOpacity="0.2"
            />
            <rect
              x="16"
              y="12"
              width="3"
              height="6"
              fill="currentColor"
              fillOpacity="0.2"
            />
          </svg>
        </Tooltip>
      );
    case 'line':
      return (
        <Tooltip label={t('lineChart')}>
          <svg
            className="h-5 w-5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path
              d="M3 18L8 12L12 16L21 6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Tooltip>
      );
  }
};

export const ChartTypeSelector: React.FC<ChartTypeSelectorProps> = ({
  value,
  onChange,
}) => {
  const options = [
    {
      value: 'area',
      label: <ChartTypeIcon type="area" />,
      className: 'data-[state=on]:text-[#FF732E]',
    },
    {
      value: 'bar',
      label: <ChartTypeIcon type="bar" />,
      className: 'data-[state=on]:text-[#FF732E]',
    },
    {
      value: 'line',
      label: <ChartTypeIcon type="line" />,
      className: 'data-[state=on]:text-[#FF732E]',
    },
  ];

  const handleChange = (newValue: string) => {
    onChange(newValue as ChartType);
  };

  return (
    <ToggleGroup
      onValueChange={handleChange}
      value={value}
      items={options}
      type="single"
    />
  );
};
